import React from "react";
import { CSVLink } from "react-csv";
import html2canvas from "html2canvas";
import Tip from "./tooltip";
import { toast } from "react-toastify";

interface ChartExportControlProps {
    data: any[];
    filename: string;
    cardId: string;
    variant?: "classic" | "dots";
}

const ChartExportControl: React.FC<ChartExportControlProps> = ({
    data,
    filename,
    cardId,
    variant = "classic",
}) => {
    const downloadImage = async (format: "png" | "jpg") => {
        const element = document.getElementById(cardId);
        if (!element) return;

        try {
            const canvas = await html2canvas(element, {
                backgroundColor: "#ffffff",
                scale: 2, // Higher quality
                useCORS: true,
                logging: false,
            });

            const link = document.createElement("a");
            link.download = `${filename}.${format}`;
            link.href = canvas.toDataURL(
                `image/${format === "jpg" ? "jpeg" : "png"}`,
                1.0
            );
            link.click();
            toast.success(`${format.toUpperCase()} downloaded successfully!`);
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Failed to download image.");
        }
    };

    const downloadSVG = () => {
        const element = document.getElementById(cardId);
        if (!element) return;
        const svg = element.querySelector("svg");
        if (!svg) {
            toast.error("SVG export is only available for charts.");
            return;
        }

        try {
            const svgData = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgData], {
                type: "image/svg+xml;charset=utf-8",
            });
            const svgUrl = URL.createObjectURL(svgBlob);
            const downloadLink = document.createElement("a");
            downloadLink.href = svgUrl;
            downloadLink.download = `${filename}.svg`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            toast.success("SVG downloaded successfully!");
        } catch (error) {
            toast.error("Failed to download SVG.");
        }
    };

    return (
        <div className="dropdown" data-html2canvas-ignore>
            <Tip text="Export Options" placement="top">
                <button
                    className={`btn btn-icon btn-sm d-flex align-items-center justify-content-center border chart-export-btn ${variant === "dots" ? "dots-variant" : ""
                        }`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                    }}
                >
                    {variant === "dots" ? (
                        <div className="dots-icon-wrapper">
                            <div className="dots-indicator">
                                <span className="dot red"></span>
                                <span className="dot yellow"></span>
                                <span className="dot green"></span>
                            </div>
                            <i
                                className="bi bi-cloud-download hover-icon"
                                style={{ fontSize: "16px" }}
                            />
                        </div>
                    ) : (
                        <i
                            className="bi bi-cloud-download"
                            style={{ fontSize: "16px" }}
                        />
                    )}
                </button>
            </Tip>
            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                <li>
                    <CSVLink
                        data={data}
                        filename={`${filename}.csv`}
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => toast.success("CSV downloaded successfully!")}
                    >
                        <i className="ti ti-file-type-csv me-2 text-success" />
                        Download CSV
                    </CSVLink>
                </li>
                <li>
                    <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={downloadSVG}
                    >
                        <i className="ti ti-file-type-svg me-2 text-warning" />
                        Download SVG
                    </button>
                </li>
                <li>
                    <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => downloadImage("png")}
                    >
                        <i className="ti ti-file-type-png me-2 text-info" />
                        Download PNG
                    </button>
                </li>
                <li>
                    <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => downloadImage("jpg")}
                    >
                        <i className="ti ti-file-type-jpg me-2 text-danger" />
                        Download JPG
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default ChartExportControl;
